import React, { ChangeEvent, useState, useEffect } from "react";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { BasePopup } from "components/pop-up/BasePopup";
import { Referral } from "types";
import { updateAssetReferral } from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";

interface CreateSuspensionRequestPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (e: React.FormEvent) => void;
  isEditMode?: boolean;
  existingData?: any;
  referralId: string;
  referral: Referral | null;
  onSuspensionCreated?: (res: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}

const defaultFormData = {
  suspension_reseaon_type: "",
  suspension_request_date: "",
  suspension_request_summary: "",
};

export const CreateSuspensionRequestPopup: React.FC<
  CreateSuspensionRequestPopupProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode = false,
  existingData,
  referralId,
  referral,
  setActiveTab,
  onSuspensionCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState(defaultFormData);

  const { showTooltip } = useTooltip();

  const actualEditMode = isEditMode || Boolean(existingData);

  useEffect(() => {
    if (isOpen && actualEditMode && existingData) {
      // Populate form with existing data
      setFormData({
        suspension_reseaon_type: existingData.suspension_reseaon_type || "",
        suspension_request_date: existingData.suspension_request_date || "",
        suspension_request_summary:
          existingData.suspension_request_summary || "",
      });
    } else if (isOpen && !actualEditMode) {
      // Reset form for new entry
      resetForm();
    }
  }, [isOpen, actualEditMode, existingData]);

  const resetForm = () => {
    setFormData(defaultFormData);
    setUploadedFiles([]);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.suspension_reseaon_type.trim()) {
      showTooltip(t("please_insert_suspension_reason_type"), "error");
      return;
    }

    if (!formData.suspension_request_date) {
      showTooltip(t("please_select_suspension_request_date"), "error");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      // Append form data
      data.append("suspension_reseaon_type", formData.suspension_reseaon_type);
      data.append("suspension_request_date", formData.suspension_request_date);
      data.append("previous_status", referral?.action_status || "");
      data.append(
        "suspension_request_summary",
        formData?.suspension_request_summary || "",
      );
      data.append("action_status", "Suspension Requested");
      // Set appropriate status based on edit mode
      // if (actualEditMode) {
      //   data.append("action_status", "Suspension Updated");
      // } else {
      //   data.append("action_status", "Suspension Requested");
      //   data.append("is_suspension_requested", "true");
      // }

      // Append files
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          data.append("suspension_request_document", file);
        });
      }

      // Update the referral with suspension data
      const response = await updateAssetReferral(referralId, data);

      const successMessage = actualEditMode
        ? "Suspension request updated successfully!"
        : "Suspension request created successfully!";

      showTooltip(successMessage, "success");
      onClose();
      resetForm();

      if (onSuspensionCreated && response) {
        onSuspensionCreated(response);
        setActiveTab?.("Suspension");
      }
    } catch (error) {
      const errorMessage = actualEditMode
        ? "Failed to update suspension request"
        : "Failed to create suspension request";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={
        actualEditMode ? "Edit Suspension Request" : "Create Suspension Request"
      }
      description={
        actualEditMode
          ? "Edit suspension request information"
          : "Create a new suspension request for this referral"
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col">
          <label className="referral-popup-label">
            Suspension Reason Type *
          </label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="suspension_reseaon_type"
            value={formData.suspension_reseaon_type}
            placeholder="Enter suspension reason type"
          />
        </div>

        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Suspension Request Date *
          </label>
          <input
            className="referral-form-field"
            onChange={handleInputChange}
            name="suspension_request_date"
            type="date"
            value={formData.suspension_request_date}
          />
        </div>
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Suspension Request Summary *
          </label>
          <textarea
            className="referral-form-field h-24"
            onChange={handleInputChange}
            name="suspension_request_summary"
            placeholder="Enter detailed suspension request summary..."
            value={formData.suspension_request_summary}
          />
        </div>
        <div className="mt-4">
          <MultipleFileUploadComponent
            uploadedFiles={uploadedFiles}
            onFilesChange={setUploadedFiles}
            label="Suspension Request Documents"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 referral-form-button text-white w-full ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading
            ? "Processing..."
            : actualEditMode
              ? "Update Suspension Request"
              : "Create Suspension Request"}
        </button>
      </form>
    </BasePopup>
  );
};

export default CreateSuspensionRequestPopup;
